import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import MainLayout from './components/Layout/MainLayout';
import { useAuth } from './contexts/AuthContext';
import Chat from './pages/Chat';
import SocialMediaPosts from './pages/SocialMediaPosts';
import ProductDescription from './pages/ProductDescription';
import CategoryDescription from './pages/CategoryDescription';
import CompanyManagement from './pages/CompanyManagement';
import DomainDetails from './pages/DomainDetails';
import InviteConfirmation from './components/InviteConfirmation';
import Newsletter from './pages/Newsletter';
import ArticleGeneration from './pages/ArticleGeneration';
import SetPassword from './components/auth/SetPassword';
import { usePermissions } from './contexts/PermissionsContext';
import AdGeneration from './pages/AdGeneration';
import Custobar from './pages/Custobar';
import Error from './pages/Error';
import Companies from './pages/Companies';
import Users from './pages/Users';
import SystemWishlist from './pages/SystemWishlist';
import SystemPromptsPage from './pages/SystemPromptsPage';
import AiModels from './pages/AiModels';
// Add a loading spinner component
const LoadingSpinner = () => (
  <div className="h-screen w-screen flex items-center justify-center">
    <div className="spinner"></div>
  </div>
);

// Create a protected route wrapper component
const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
}: {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
}) => {
  const { user, loading } = useAuth();
  const { hasAdminAccess, isSuperAdmin } = usePermissions();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireSuperAdmin && !isSuperAdmin()) {
    return <Navigate to="/chat" replace />;
  }

  if (requireAdmin && !hasAdminAccess()) {
    return <Navigate to="/chat" replace />;
  }

  return <MainLayout>{children}</MainLayout>;
};

export default function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <Routes>
        <Route path="/login" element={!user ? <Login /> : <Navigate to="/chat" replace />} />
        <Route path="/invite-confirmation" element={<InviteConfirmation />} />
        <Route path="/auth/confirm" element={<SetPassword />} />
        <Route path="/auth/error" element={<Error />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Navigate to="/chat" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/companies"
          element={
            <ProtectedRoute requireAdmin>
              <Companies />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute requireSuperAdmin>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="/system-wishlist"
          element={
            <ProtectedRoute requireSuperAdmin>
              <SystemWishlist />
            </ProtectedRoute>
          }
        />
        <Route
          path="/system-prompts"
          element={
            <ProtectedRoute requireSuperAdmin>
              <SystemPromptsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-models"
          element={
            <ProtectedRoute requireSuperAdmin>
              <AiModels />
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:companyId"
          element={
            <ProtectedRoute requireAdmin>
              <CompanyManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:companyId/:domainId"
          element={
            <ProtectedRoute requireAdmin>
              <DomainDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          }
        />
        <Route
          path="/newsletter"
          element={
            <ProtectedRoute>
              <Newsletter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/article-generation"
          element={
            <ProtectedRoute>
              <ArticleGeneration />
            </ProtectedRoute>
          }
        />
        {/* Critic and writer models live under Company details since 2026-09-10. */}
        <Route path="/critic-models" element={<Navigate to="/companies" replace />} />
        {/* These pages became tabs; the old URLs redirect to their new homes. */}
        <Route
          path="/article-history"
          element={<Navigate to="/article-generation?tab=history" replace />}
        />
        <Route
          path="/article-prompts"
          element={<Navigate to="/system-prompts?tab=article" replace />}
        />
        <Route
          path="/social-media-posts"
          element={
            <ProtectedRoute>
              <SocialMediaPosts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ad-generation"
          element={
            <ProtectedRoute>
              <AdGeneration />
            </ProtectedRoute>
          }
        />
        <Route
          path="/product-description"
          element={
            <ProtectedRoute>
              <ProductDescription />
            </ProtectedRoute>
          }
        />
        <Route
          path="/category-description"
          element={
            <ProtectedRoute>
              <CategoryDescription />
            </ProtectedRoute>
          }
        />
        <Route
          path="/custobar"
          element={
            <ProtectedRoute>
              <Custobar />
            </ProtectedRoute>
          }
        />

        {/* Catch-all route for any unmatched paths */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
